import { useMediaQuery } from '@mantine/hooks'
import { Typography } from '../common/typography'
import { useLocation, useNavigate, useParams } from 'react-router'
import {
  IconAlertCircle,
  IconChevronLeft,
  IconCircleCheck,
  IconHistoryToggle,
  IconHourglass,
  IconLoader,
  IconRefreshOff,
  IconStar,
} from '@tabler/icons-react'
import classes from './Course.module.css'
import Header from '../common/components/header/Header'
import Meta from '../common/Meta'
import clsx from 'clsx'
import { useCourseDetails, type QuestionGroup } from './api'
import dayjs from 'dayjs'
import { ReactComponent as Learning } from '../assets/learning.svg'
import DOMPurify from 'dompurify'
import { Alert, Button, Text } from '@mantine/core'

function findFirstUnansweredQuestionGroup(
  questionGroups: QuestionGroup[],
  answeredQuestionIds: number[]
): number | null {
  for (const group of questionGroups) {
    const hasUnansweredQuestion = group.questions.some(
      (q) => !answeredQuestionIds.includes(q.question_id)
    )
    if (hasUnansweredQuestion) {
      return group.question_group_id
    }
  }
  return null
}

export default function Course() {
  const navigate = useNavigate()
  const location = useLocation()
  const { courseId } = useParams<{ courseId: string }>()
  const isDesktop = useMediaQuery('(min-width: 800px)')

  const { data: courseDetails } = useCourseDetails({
    course_id: Number(courseId),
  })

  const isInProgress = courseDetails.status === 'in_progress'
  const isFailed = courseDetails.status === 'failed'
  const isNotStarted = courseDetails.status === 'not_started'
  const isCompleted = courseDetails.status === 'completed'

  const passMarkPercent = parseFloat(courseDetails.pass_mark)

  const hasUnlimitedAttempts = courseDetails.quiz_retry_attempts === 0
  const canRetry =
    hasUnlimitedAttempts ||
    Number(courseDetails.quiz_retry_attempts) > courseDetails.attempts_count

  const isTimedQuiz = Boolean(courseDetails.duration_seconds)
  const cannotResumeTimedQuiz = isInProgress && isTimedQuiz

  const isHardDeadlinePassed =
    courseDetails.hard_deadline &&
    dayjs().isAfter(dayjs(courseDetails.hard_deadline))

  const shouldShowButton =
    !isHardDeadlinePassed &&
    (isNotStarted || (isInProgress && !isTimedQuiz) || (isFailed && canRetry))

  const shouldShowAlert =
    isHardDeadlinePassed ||
    isCompleted ||
    (isFailed && !canRetry) ||
    cannotResumeTimedQuiz

  const handleStartQuiz = () => {
    if (isInProgress) {
      if (!courseDetails.in_progress_attempt) {
        console.error('No in_progress_attempt data found for in_progress quiz')
        return
      }

      const { attempt_id, answered_question_ids } =
        courseDetails.in_progress_attempt

      const firstUnansweredGroupId = findFirstUnansweredQuestionGroup(
        courseDetails.question_groups,
        answered_question_ids
      )

      if (!firstUnansweredGroupId) {
        console.error('All questions answered but quiz marked as in_progress')
        const firstGroupId = courseDetails.question_groups[0]?.question_group_id
        if (firstGroupId) {
          navigate(
            `/learning/course/${courseId}/quiz/${attempt_id}/question-group/${firstGroupId}`
          )
        }
        return
      }

      navigate(
        `/learning/course/${courseId}/quiz/${attempt_id}/question-group/${firstUnansweredGroupId}`
      )
      return
    }

    if (isNotStarted || (isFailed && canRetry)) {
      navigate(`/learning/course/${courseId}/consent`)
    }
  }

  return (
    <div className={classes.container}>
      <div className={classes.headerContainer}>
        {isDesktop && (
          <IconChevronLeft
            onClick={() =>
              navigate(location.state?.from?.pathname || '/learning')
            }
            className={classes.backIcon}
          />
        )}
        <Header
          title="Learning XP"
          back={location.state?.from?.pathname || '/learning'}
        />
      </div>
      <Meta title="Learning XP" />
      <div
        className={clsx(classes.courseWrapper, {
          [classes.courseWrapperMobile]: !isDesktop,
        })}
      >
        <div
          className={clsx(classes.courseContainer, {
            [classes.courseContainerMobile]: !isDesktop,
          })}
        >
          <div className={classes.courseDetailsContainer}>
            <div className={classes.courseDetailsHeader}>
              <Typography fs="xs" fw={500} lh="130" tt="uppercase">
                Course Details
              </Typography>
              <Typography fs="xxl" fw={700} level={2} lh="100" ta="center">
                {courseDetails.title}
              </Typography>
            </div>
            <Learning className={classes.learningIcon} />
            <div className={classes.courseInformationsContainer}>
              <div className={classes.informationRow}>
                <IconLoader className={classes.informationIcon} />
                <div className={classes.statusRow}>
                  <Typography lh="150">Status: </Typography>
                  <Typography
                    fw={700}
                    lh="150"
                    fc={{
                      light:
                        courseDetails.status === 'completed'
                          ? 'teal-9'
                          : courseDetails.status === 'in_progress'
                            ? 'orange-7'
                            : courseDetails.status === 'failed'
                              ? 'red-9'
                              : 'gray-6',
                      dark:
                        courseDetails.status === 'completed'
                          ? 'teal-9'
                          : courseDetails.status === 'in_progress'
                            ? 'orange-7'
                            : 'gray-6',
                    }}
                  >
                    {courseDetails.status === 'not_started'
                      ? 'Not Started'
                      : courseDetails.status === 'in_progress'
                        ? 'In progress'
                        : courseDetails.status === 'failed'
                          ? 'Failed'
                          : 'Completed'}
                  </Typography>
                </div>
              </div>
              {courseDetails.soft_deadline && (
                <div className={classes.informationRow}>
                  <IconHistoryToggle className={classes.informationIcon} />
                  <Typography lh="150">
                    Soft Deadline:{' '}
                    {dayjs(courseDetails.soft_deadline).format('DD/MM/YYYY')}
                    {courseDetails.soft_deadline_penalty > 0 &&
                      ` (-${courseDetails.soft_deadline_penalty} XP penalty)`}
                  </Typography>
                </div>
              )}
              {courseDetails.hard_deadline && (
                <div className={classes.informationRow}>
                  <IconHistoryToggle className={classes.informationIcon} />
                  <Typography lh="150" fw={600}>
                    Hard Deadline:{' '}
                    {dayjs(courseDetails.hard_deadline).format('DD/MM/YYYY')}
                  </Typography>
                </div>
              )}
              {courseDetails.duration_seconds && (
                <div className={classes.informationRow}>
                  <IconHourglass className={classes.informationIcon} />
                  <Typography lh="150">
                    Quiz Time:
                    {dayjs()
                      .startOf('day')
                      .add(courseDetails.duration_seconds, 'seconds')
                      .format('mm:ss')}
                  </Typography>
                </div>
              )}

              <div className={classes.informationRow}>
                <IconRefreshOff className={classes.informationIcon} />
                <Typography lh="150">
                  Quiz Attempts: {courseDetails.quiz_retry_attempts}
                </Typography>
              </div>
              <div className={classes.informationRow}>
                <IconCircleCheck className={classes.informationIcon} />
                <Typography lh="150">
                  Pass mark: {passMarkPercent.toFixed(1)}%
                </Typography>
              </div>
              <div className={classes.informationRow}>
                <IconStar className={classes.informationIcon} />
                <Typography fw={600} lh="150">
                  Max Quiz Score: {courseDetails.learning_xp.toFixed(1)} points
                </Typography>
              </div>
            </div>
            <Typography fs="xs" lh="130">
              Complete the course to unlock the quiz.
            </Typography>
          </div>
          <div className={classes.courseDescriptionContainer}>
            <div className={classes.courseDescriptionHeader}>
              <Typography fs="xxl" fw={700} level={2} lh="100">
                {courseDetails.title}
              </Typography>
              <Typography
                fs="xs"
                fc={{ light: 'gray-5', dark: 'gray-5' }}
                lh="130"
              >
                {dayjs(courseDetails.created_at).format('DD/MM/YYYY, HH:mm')}
              </Typography>
            </div>
            <Text
              component="p"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(courseDetails.description),
              }}
            />
            {shouldShowAlert ? (
              isHardDeadlinePassed ? (
                <Alert
                  icon={<IconAlertCircle />}
                  classNames={{
                    root: classes.alertRoot,
                    wrapper: classes.alertWrapper,
                    message: classes.alertMessage,
                    icon: classes.alertIcon,
                  }}
                  data-status="error"
                >
                  <Typography fc={{ light: 'red-0', dark: 'red-0' }} lh="150">
                    Quiz hard deadline has passed and quiz cannot be taken.
                  </Typography>
                </Alert>
              ) : isCompleted ? (
                <Alert
                  icon={<IconCircleCheck />}
                  classNames={{
                    root: classes.alertRoot,
                    wrapper: classes.alertWrapper,
                    message: classes.alertMessage,
                    icon: classes.alertIcon,
                  }}
                  data-status="success"
                >
                  <Typography fc={{ light: 'teal-0', dark: 'teal-0' }} lh="150">
                    Quiz already completed
                  </Typography>
                </Alert>
              ) : cannotResumeTimedQuiz ? (
                <Alert
                  icon={<IconHourglass />}
                  classNames={{
                    root: classes.alertRoot,
                    wrapper: classes.alertWrapper,
                    message: classes.alertMessage,
                    icon: classes.alertIcon,
                  }}
                  data-status="idle"
                >
                  <Typography
                    fc={{ light: 'orange-0', dark: 'orange-0' }}
                    lh="150"
                  >
                    Timed quizzes cannot be resumed. Your quiz will be submitted
                    automatically when the time expires.
                  </Typography>
                </Alert>
              ) : (
                <Alert
                  icon={<IconAlertCircle />}
                  classNames={{
                    root: classes.alertRoot,
                    wrapper: classes.alertWrapper,
                    message: classes.alertMessage,
                    icon: classes.alertIcon,
                  }}
                  data-status="error"
                >
                  <Typography fc={{ light: 'red-0', dark: 'red-0' }} lh="150">
                    Quiz already completed
                  </Typography>
                </Alert>
              )
            ) : shouldShowButton ? (
              <Button className={classes.button} onClick={handleStartQuiz}>
                <Typography
                  fc={{ light: 'white', dark: 'white' }}
                  lh="150"
                  tt="uppercase"
                >
                  {isInProgress
                    ? 'Resume Quiz'
                    : isFailed
                      ? 'Retry Quiz'
                      : 'Start Quiz'}
                </Typography>
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}
